import { useEffect, useState } from "react";
import { Download, Bell, BellRing, Share, PlusSquare, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

let deferredPrompt = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferredPrompt = e; });
}

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);

function urlB64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export function InstallButton({ variant = "icon" }) {
  const [installed, setInstalled] = useState(isStandalone());
  const [showIOS, setShowIOS] = useState(false);

  useEffect(() => {
    const onInstalled = () => setInstalled(true);
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, []);

  if (installed) return null;

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") { toast.success("App installed! Find Gulf Coast VIP on your home screen."); deferredPrompt = null; }
      return;
    }
    if (isIOS()) { setShowIOS(true); return; }
    toast.info("To install: open your browser menu and tap 'Install app' or 'Add to Home Screen'.");
  };

  return (
    <>
      {variant === "icon" ? (
        <button onClick={install} title="Download App" data-testid="pwa-install-btn"
          className="w-9 h-9 flex items-center justify-center rounded-full glass hover:border-white/30 text-zinc-300 hover:text-white transition-colors">
          <Download size={16} />
        </button>
      ) : (
        <button onClick={install} data-testid="pwa-install-btn-menu" className="flex items-center gap-2 text-zinc-200">
          <Download size={16} className="text-rose-400" /> Download App
        </button>
      )}
      {showIOS && (
        <div className="fixed inset-0 z-[100] bg-black/70 flex items-end sm:items-center justify-center p-4" onClick={() => setShowIOS(false)}>
          <div className="bg-[#111] border border-white/15 rounded-2xl p-6 max-w-sm w-full relative" onClick={(e) => e.stopPropagation()} data-testid="ios-install-dialog">
            <button onClick={() => setShowIOS(false)} className="absolute top-3 right-3 text-zinc-500 hover:text-white"><X size={18} /></button>
            <img src="/icons/icon-192.png" alt="" className="w-14 h-14 rounded-xl mb-4" />
            <h3 className="font-display font-bold text-lg mb-2">Install Gulf Coast VIP</h3>
            <p className="text-sm text-zinc-400 mb-4">Add the app to your home screen for the full experience:</p>
            <ol className="space-y-3 text-sm text-zinc-300">
              <li className="flex items-center gap-3"><Share size={18} className="text-rose-400 shrink-0" /> 1. Tap the <b>Share</b> button in Safari</li>
              <li className="flex items-center gap-3"><PlusSquare size={18} className="text-rose-400 shrink-0" /> 2. Tap <b>Add to Home Screen</b></li>
              <li className="flex items-center gap-3"><Download size={18} className="text-rose-400 shrink-0" /> 3. Tap <b>Add</b> — done!</li>
            </ol>
          </div>
        </div>
      )}
    </>
  );
}

export function NotificationsButton({ variant = "icon" }) {
  const [state, setState] = useState("idle"); // idle | subscribed | busy | unsupported

  useEffect(() => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) { setState("unsupported"); return; }
    navigator.serviceWorker.ready.then((reg) => reg.pushManager.getSubscription()).then((sub) => {
      if (sub) setState("subscribed");
    }).catch(() => {});
  }, []);

  if (state === "unsupported") return null;

  const toggle = async () => {
    if (state === "busy") return;
    setState("busy");
    try {
      const reg = await navigator.serviceWorker.ready;
      const existing = await reg.pushManager.getSubscription();
      if (existing) {
        await api.post("/push/unsubscribe", { endpoint: existing.endpoint });
        await existing.unsubscribe();
        setState("idle");
        toast.success("Notifications turned off.");
        return;
      }
      if (isIOS() && !isStandalone()) {
        setState("idle");
        toast.info("On iPhone, first install the app (Download button), then enable notifications from inside the app.");
        return;
      }
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setState("idle"); toast.error("Notifications permission was denied."); return; }
      const { data } = await api.get("/push/vapid-public-key");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(data.key),
      });
      await api.post("/push/subscribe", { subscription: sub.toJSON(), user_agent: navigator.userAgent });
      setState("subscribed");
      toast.success("Notifications enabled! You'll get updates on events, deals & spotlights.");
    } catch (e) {
      setState("idle");
      toast.error("Could not enable notifications on this device.");
    }
  };

  const Icon = state === "subscribed" ? BellRing : Bell;
  return variant === "icon" ? (
    <button onClick={toggle} title={state === "subscribed" ? "Notifications on" : "Enable notifications"} data-testid="pwa-notify-btn"
      className={`w-9 h-9 flex items-center justify-center rounded-full glass hover:border-white/30 transition-colors ${state === "subscribed" ? "text-rose-400" : "text-zinc-300 hover:text-white"}`}>
      <Icon size={16} />
    </button>
  ) : (
    <button onClick={toggle} data-testid="pwa-notify-btn-menu" className="flex items-center gap-2 text-zinc-200">
      <Icon size={16} className="text-rose-400" /> {state === "subscribed" ? "Notifications On" : "Enable Notifications"}
    </button>
  );
}
